import { makeAutoObservable, runInAction } from 'mobx'

import { serverMessage } from '@/Ship/Helpers/serverError'

import { LETTER_TEXTS as T } from '../Contracts/ILetter'
import { LoadOwnerLettersTask } from '../Tasks/LoadOwnerLettersTask'
import { RemoveLetterTask } from '../Tasks/RemoveLetterTask'

import type { ILetter, LetterOwnerField } from '../Contracts/ILetter'

/**
 * Листи одного власника у вкладці «Листи» його картки (`SPEC-013` §9.2):
 * сторінка списку, перечитування після відправки й видалення.
 *
 * Стан — тут, а не в ядровому списку: вкладений `ResourceList` чужого ресурсу
 * в картці підхоплює її контекст (`CORE-ISSUES` §3) — так само зроблено
 * вкладки «Справи», «Договори», «Друковані форми».
 */
export class OwnerLettersStore {
    items: ILetter[] = []
    total = 0
    page = 1
    isLoading = false
    failed = false
    removingUuid: string | null = null

    private ownerUuid: string | null = null
    private requestId = 0

    constructor(
        private readonly ownerField: LetterOwnerField,
        readonly pageSize: number
    ) {
        makeAutoObservable<OwnerLettersStore, 'ownerField' | 'ownerUuid' | 'requestId'>(
            this,
            { ownerField: false, ownerUuid: false, requestId: false, pageSize: false },
            { autoBind: true }
        )
    }

    /** Відкрили вкладку іншого власника — з першої сторінки. */
    bind(ownerUuid: string | undefined): void {
        if (!ownerUuid || ownerUuid === this.ownerUuid) return

        this.ownerUuid = ownerUuid
        this.page = 1
        void this.load()
    }

    setPage(page: number): void {
        this.page = page
        void this.load()
    }

    reload(): void {
        void this.load()
    }

    /**
     * Видаляє лист (`SPEC-013` §7.8) і перечитує сторінку. Підтвердження —
     * справа меню рядка. Повертає текст помилки або `null`.
     */
    async remove(uuid: string): Promise<string | null> {
        if (this.removingUuid !== null) return null

        this.removingUuid = uuid

        try {
            await RemoveLetterTask(uuid)
            await this.load()

            return null
        } catch (error) {
            return serverMessage(error, T.contactAdmin)
        } finally {
            runInAction(() => {
                this.removingUuid = null
            })
        }
    }

    private async load(): Promise<void> {
        const ownerUuid = this.ownerUuid

        if (!ownerUuid) return

        const requestId = ++this.requestId

        this.isLoading = true
        this.failed = false

        try {
            const { items, total } = await LoadOwnerLettersTask({
                ownerField: this.ownerField,
                ownerUuid,
                page: this.page,
                pageSize: this.pageSize,
            })

            if (requestId !== this.requestId) return

            runInAction(() => {
                this.items = items
                this.total = total
            })
        } catch {
            if (requestId !== this.requestId) return

            runInAction(() => {
                this.items = []
                this.total = 0
                this.failed = true
            })
        } finally {
            if (requestId === this.requestId) {
                runInAction(() => {
                    this.isLoading = false
                })
            }
        }
    }
}
